import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Alternador, Entrada, Rotulo, Selecao, type Opcao } from '../../componentes/formulario';
import { numero } from '../../nucleo/formato';

/** Linha do rateio em edição: o percentual fica em texto para aceitar "33,3333" enquanto se digita. */
export interface LinhaRateio {
  chave: string;
  centro_custo_id: string;
  percentual: string;
}

export const novaLinhaRateio = (): LinhaRateio => ({ chave: crypto.randomUUID(), centro_custo_id: '', percentual: '' });

const pct = (v: string) => {
  const n = Number(v.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

/** Soma dos percentuais em décimos de milésimo (4 casas, sem erro de ponto flutuante). */
export const somaRateio = (linhas: LinhaRateio[]) => linhas.reduce((a, l) => a + Math.round(pct(l.percentual) * 1e4), 0) / 1e4;

/**
 * Rateio no formato da API (`rateio: [{ centro_custo_id, percentual }]`, soma 100, cada centro uma vez). Linha vazia
 * sai; lança Error com a mensagem pronta quando o rateio não fecha (a API confere de novo e recusa com `rateio.invalido`).
 */
export function rateioParaApi(linhas: LinhaRateio[], msg: { incompleto: string; repetido: string }): { centro_custo_id: string; percentual: number }[] {
  const preenchidas = linhas.filter((l) => l.centro_custo_id || l.percentual.trim());
  if (!preenchidas.length) throw new Error(msg.incompleto);
  const vistos = new Set<string>();
  for (const l of preenchidas) {
    if (!l.centro_custo_id || pct(l.percentual) <= 0 || pct(l.percentual) > 100) throw new Error(msg.incompleto);
    if (vistos.has(l.centro_custo_id)) throw new Error(msg.repetido);
    vistos.add(l.centro_custo_id);
  }
  if (Math.round(somaRateio(preenchidas) * 1e4) !== 100 * 1e4) throw new Error(msg.incompleto);
  return preenchidas.map((l) => ({ centro_custo_id: l.centro_custo_id, percentual: Math.round(pct(l.percentual) * 1e4) / 1e4 }));
}

/**
 * Fase 2 (W14): rateio editável na tela do documento (a API já aceitava desde a W7). Ligado, o centro único some e as
 * linhas repartem os itens sem centro próprio entre os centros, pelo maior resto. Só na emissão: as linhas de
 * competência do documento emitido não mudam.
 */
export function RateioEditor({ ativo, aoAtivar, linhas, aoMudar, centros }: { ativo: boolean; aoAtivar: (v: boolean) => void; linhas: LinhaRateio[]; aoMudar: (v: LinhaRateio[]) => void; centros: Opcao[] }) {
  const { t } = useTranslation('contas');
  const muda = (chave: string, parcial: Partial<LinhaRateio>) => aoMudar(linhas.map((l) => (l.chave === chave ? { ...l, ...parcial } : l)));
  const soma = somaRateio(linhas);
  return (
    <fieldset className="flex min-w-0 flex-col gap-3 sm:col-span-6" data-rateio-editor>
      <legend className="mb-1 text-sm font-medium text-tinta-2">{t('w14.rateio')}</legend>
      <div className="flex items-center gap-2">
        <Alternador id="rateio-ativo" marcado={ativo} aoMudar={aoAtivar} rotulo={t('w14.ratear')} desabilitado={!centros.length} />
        <label htmlFor="rateio-ativo" className="text-sm text-tinta-2">
          {t('w14.ratear')}
        </label>
      </div>
      {!centros.length && <p className="text-xs text-tinta-3">{t('w14.semCentros')}</p>}
      {ativo && (
        <>
          <p className="text-xs text-tinta-3">{t('w14.rateioAjuda')}</p>
          {linhas.map((l, n) => (
            <div key={l.chave} className="grid min-w-0 grid-cols-6 items-end gap-2 rounded-lg border border-borda bg-sup-1 p-3">
              <div className="col-span-6 sm:col-span-3">
                <Rotulo id={`rateio-centro-${l.chave}`} rotulo={t('w14.centroN', { n: n + 1 })}>
                  <Selecao id={`rateio-centro-${l.chave}`} vazio={t('w7.semCentro')} opcoes={centros} value={l.centro_custo_id} onChange={(e) => muda(l.chave, { centro_custo_id: e.target.value })} />
                </Rotulo>
              </div>
              <div className="col-span-5 sm:col-span-2">
                <Rotulo id={`rateio-pct-${l.chave}`} rotulo={t('w14.percentual')}>
                  <Entrada id={`rateio-pct-${l.chave}`} inputMode="decimal" className="numero text-right" value={l.percentual} onChange={(e) => muda(l.chave, { percentual: e.target.value })} />
                </Rotulo>
              </div>
              <div className="col-span-1 flex justify-end">
                <Botao
                  variante="fantasma"
                  aria-label={t('w14.removerCentro', { n: n + 1 })}
                  title={t('w14.removerCentro', { n: n + 1 })}
                  disabled={linhas.length === 1}
                  onClick={() => aoMudar(linhas.filter((x) => x.chave !== l.chave))}
                  icone={<Trash2 className="size-4" />}
                />
              </div>
            </div>
          ))}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Botao icone={<Plus className="size-4" />} disabled={linhas.length >= centros.length} onClick={() => aoMudar([...linhas, novaLinhaRateio()])}>
              {t('w14.adicionarCentro')}
            </Botao>
            <p className={`numero text-sm ${Math.round(soma * 1e4) === 100 * 1e4 ? 'text-tinta-2' : 'text-laranja'}`}>{t('w14.somaRateio', { soma: numero(soma, Number.isInteger(soma) ? 0 : 4) })}</p>
          </div>
        </>
      )}
    </fieldset>
  );
}
